import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/i18n/useTranslation";
import { Cookie, Settings, X } from "lucide-react";

const STORAGE_KEY = "rankaro_cookie_consent";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  thirdParty: boolean;
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    thirdParty: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
  }, []);

  const saveAndClose = (prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  const handleAcceptAll = () =>
    saveAndClose({ essential: true, analytics: true, thirdParty: true });

  const handleEssentialOnly = () =>
    saveAndClose({ essential: true, analytics: false, thirdParty: false });

  const handleSaveCustom = () => saveAndClose(preferences);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-fade-in">
      <div className="max-w-2xl mx-auto backdrop-blur-xl bg-card/80 border border-border/50 rounded-2xl shadow-2xl p-5 md:p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-sm">
              {t("cookieConsent.title")}
            </h3>
            <p className="text-muted-foreground text-xs mt-1 leading-relaxed">
              {t("cookieConsent.description")}{" "}
              <Link
                to="/cookies"
                className="text-primary hover:underline underline-offset-2"
              >
                {t("cookieConsent.learnMore")}
              </Link>
            </p>
          </div>
        </div>

        {/* Customize panel */}
        {showCustomize && (
          <div className="border border-border/30 rounded-xl p-4 mb-4 space-y-3 bg-muted/30">
            {/* Essential */}
            <label className="flex items-start gap-3">
              <Checkbox checked disabled className="mt-0.5" />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("cookieConsent.essential")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("cookieConsent.essentialDesc")}
                </p>
              </div>
            </label>
            {/* Analytics */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={preferences.analytics}
                onCheckedChange={(v) =>
                  setPreferences((p) => ({ ...p, analytics: !!v }))
                }
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("cookieConsent.analytics")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("cookieConsent.analyticsDesc")}
                </p>
              </div>
            </label>
            {/* Third party */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={preferences.thirdParty}
                onCheckedChange={(v) =>
                  setPreferences((p) => ({ ...p, thirdParty: !!v }))
                }
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("cookieConsent.thirdParty")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("cookieConsent.thirdPartyDesc")}
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          {showCustomize ? (
            <Button onClick={handleSaveCustom} size="sm" className="flex-1">
              {t("cookieConsent.save")}
            </Button>
          ) : (
            <>
              <Button onClick={handleAcceptAll} size="sm" variant="hero" className="flex-1">
                {t("cookieConsent.acceptAll")}
              </Button>
              <Button onClick={handleEssentialOnly} size="sm" variant="outline" className="flex-1">
                {t("cookieConsent.essentialOnly")}
              </Button>
              <Button
                onClick={() => setShowCustomize(true)}
                size="sm"
                variant="ghost"
                className="flex-1 gap-1.5"
              >
                <Settings className="h-3.5 w-3.5" />
                {t("cookieConsent.customize")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
